// ═══════════════════════════════════════════════════════════════════════════
// PAGE: ADMIN AUDIT LOG
// Migration S4.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
//
// Consultation read-only des actions sensibles tracées (login success/fail,
// CRUD users/groupes/projets, exports, etc.).
//
// Filtres : action (dropdown), user_trigram (texte), période (since/until),
// pagination (50/page par défaut, max 200).
//
// Permission requise : audit_log.read (gérée côté router, le middleware
// renvoie 403 si manque).
// ═══════════════════════════════════════════════════════════════════════════

interface AuditLogEntry {
  ts: string;
  action: string;
  user_trigram?: string | null;
  target_type?: string | null;
  target_id?: number | null;
  ip?: string | null;
  details?: unknown;
}

interface AuditLogResponse {
  items: AuditLogEntry[];
  total: number;
}

interface AuditLogState {
  action: string;
  user_trigram: string;
  since: string;
  until: string;
  limit: number;
  offset: number;
}

declare global {
  interface Window {
    formatTs: typeof formatTs;
  }
}

pages.adminAuditLog = async function () {
  setHeader(
    t('audit_log.page_title'),
    `<button class="btn btn-secondary btn-sm" id="al-refresh" title="${esc(t('audit_log.btn.refresh_title'))}">${esc(t('audit_log.btn.refresh'))}</button>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  const state: AuditLogState = {
    action: '',
    user_trigram: '',
    since: '',
    until: '',
    limit: 50,
    offset: 0,
  };

  let actionsList: string[] = [];
  try {
    actionsList = (await api.getAuditLogActions()) as string[];
  } catch (e: unknown) {
    safeWarn('api.getAuditLogActions', e);
  }

  async function render(): Promise<void> {
    const params: Record<string, string> = {};
    if (state.action) params['action'] = state.action;
    if (state.user_trigram) params['user_trigram'] = state.user_trigram.trim().toUpperCase();
    if (state.since) params['since'] = state.since + 'T00:00:00';
    if (state.until) params['until'] = state.until + 'T23:59:59';
    params['limit'] = String(state.limit);
    params['offset'] = String(state.offset);

    let data: AuditLogResponse;
    try {
      data = (await api.getAuditLog(params)) as AuditLogResponse;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('common.error');
      setContent(`<div class="error">${esc(msg)}</div>`);
      return;
    }

    const items = data.items || [];
    const total = data.total || 0;
    const page = Math.floor(state.offset / state.limit) + 1;
    const totalPages = Math.max(1, Math.ceil(total / state.limit));

    const actionOpts = [`<option value="">${esc(t('audit_log.filter.action_all'))}</option>`]
      .concat(
        actionsList.map(
          (a) =>
            `<option value="${esc(a)}"${a === state.action ? ' selected' : ''}>${esc(a)}</option>`,
        ),
      )
      .join('');

    const rowsHtml = items.length
      ? items
          .map(
            (r) => `
          <tr>
            <td style="white-space:nowrap;font-size:11px;color:var(--text3)">${esc(formatTs(r.ts))}</td>
            <td><code style="font-size:11px">${esc(r.action)}</code></td>
            <td>${r.user_trigram ? `<strong>${esc(r.user_trigram)}</strong>` : `<span style="color:var(--text3)">—</span>`}</td>
            <td>${r.target_type ? `${esc(r.target_type)}${r.target_id ? ' #' + r.target_id : ''}` : ''}</td>
            <td style="font-size:11px;color:var(--text3)">${esc(r.ip || '')}</td>
            <td style="font-size:11px"><pre style="margin:0;white-space:pre-wrap;font-size:10px">${r.details ? esc(JSON.stringify(r.details, null, 1)) : ''}</pre></td>
          </tr>`,
          )
          .join('')
      : `<tr><td colspan="6" style="text-align:center;padding:24px;color:var(--text3)">${esc(t('audit_log.empty'))}</td></tr>`;

    const html = `
      <div style="max-width:1200px;margin:0 auto">
        <div class="card" style="margin-bottom:12px;padding:12px 16px">
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:8px;align-items:end">
            <label>
              <small class="muted">${esc(t('audit_log.filter.action'))}</small>
              <select id="al-action" style="width:100%;padding:6px;border:1px solid var(--border2);border-radius:4px">${actionOpts}</select>
            </label>
            <label>
              <small class="muted">${esc(t('audit_log.filter.trigram'))}</small>
              <input type="text" id="al-trigram" maxlength="4" value="${esc(state.user_trigram)}" placeholder="JMA" style="width:100%;padding:6px;border:1px solid var(--border2);border-radius:4px;text-transform:uppercase">
            </label>
            <label>
              <small class="muted">${esc(t('audit_log.filter.since'))}</small>
              <input type="date" id="al-since" value="${esc(state.since)}" style="width:100%;padding:6px;border:1px solid var(--border2);border-radius:4px">
            </label>
            <label>
              <small class="muted">${esc(t('audit_log.filter.until'))}</small>
              <input type="date" id="al-until" value="${esc(state.until)}" style="width:100%;padding:6px;border:1px solid var(--border2);border-radius:4px">
            </label>
            <div style="display:flex;flex-direction:column;align-items:flex-start">
              <small class="muted" style="visibility:hidden">.</small>
              <button class="btn btn-secondary btn-sm" id="al-reset" style="white-space:nowrap">${esc(t('audit_log.btn.reset'))}</button>
            </div>
          </div>
        </div>

        <div class="card" style="padding:12px 16px">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;font-size:13px;color:var(--text3)">
            <span>${esc(t('audit_log.summary', { total, page, totalPages }))}</span>
            <span>
              <button class="btn btn-secondary btn-sm" id="al-prev" ${state.offset === 0 ? 'disabled' : ''}>${esc(t('audit_log.btn.prev'))}</button>
              <button class="btn btn-secondary btn-sm" id="al-next" ${state.offset + state.limit >= total ? 'disabled' : ''}>${esc(t('audit_log.btn.next'))}</button>
            </span>
          </div>
          <div class="table-wrap">
            <table class="table" style="width:100%;font-size:12px">
              <thead>
                <tr>
                  <th style="text-align:left">${esc(t('audit_log.col.ts'))}</th>
                  <th style="text-align:left">${esc(t('vocabulary.action'))}</th>
                  <th style="text-align:left">${esc(t('audit_log.col.actor'))}</th>
                  <th style="text-align:left">${esc(t('audit_log.col.target'))}</th>
                  <th style="text-align:left">${esc(t('audit_log.col.ip'))}</th>
                  <th style="text-align:left">${esc(t('audit_log.col.details'))}</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </div>
        </div>
      </div>
    `;
    setContent(html);

    const debounce = <Args extends unknown[]>(
      fn: (...args: Args) => void,
      ms = 300,
    ): ((...args: Args) => void) => {
      let timer: ReturnType<typeof setTimeout> | null = null;
      return (...args: Args) => {
        if (timer !== null) clearTimeout(timer);
        timer = setTimeout(() => fn(...args), ms);
      };
    };

    document.getElementById('al-action')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      if (!target) return;
      state.action = target.value;
      state.offset = 0;
      void render();
    });
    document.getElementById('al-trigram')?.addEventListener(
      'input',
      debounce((e: Event) => {
        const target = e.target as HTMLInputElement | null;
        if (!target) return;
        state.user_trigram = target.value;
        state.offset = 0;
        void render();
      }),
    );
    document.getElementById('al-since')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (!target) return;
      state.since = target.value;
      state.offset = 0;
      void render();
    });
    document.getElementById('al-until')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (!target) return;
      state.until = target.value;
      state.offset = 0;
      void render();
    });
    document.getElementById('al-reset')?.addEventListener('click', () => {
      state.action = '';
      state.user_trigram = '';
      state.since = '';
      state.until = '';
      state.offset = 0;
      void render();
    });
    document.getElementById('al-prev')?.addEventListener('click', () => {
      state.offset = Math.max(0, state.offset - state.limit);
      void render();
    });
    document.getElementById('al-next')?.addEventListener('click', () => {
      state.offset = state.offset + state.limit;
      void render();
    });
  }

  await render();

  document.getElementById('al-refresh')?.addEventListener('click', () => {
    void render();
  });
};

function formatTs(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// ── ESM globals shim (lot B.8 + S4.2) ──
window.formatTs = formatTs;

export {};
